import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import axios from 'axios';
import Layout from '../AppLayout';

const PageContainer = styled.div`
  position: relative;
  z-index: 10;
  padding: 120px 40px 40px;
  max-width: 900px;
  margin: 0 auto;
`;

const PageTitle = styled(motion.h1)`
  font-family: 'Orbitron', sans-serif;
  font-size: 36px;
  color: var(--primary);
  margin-bottom: 10px;
`;

const PageSubtitle = styled(motion.p)`
  color: var(--text-secondary);
  margin-bottom: 40px;
  font-size: 16px;
`;

const ConfigCard = styled(motion.div)`
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 30px;
  margin-bottom: 20px;
`;

const ConfigTitle = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 18px;
  color: var(--primary);
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 30px;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 10px;
`;

const Tab = styled.button`
  background: ${props => props.$active ? 'rgba(0, 240, 255, 0.15)' : 'transparent'};
  color: ${props => props.$active ? 'var(--primary)' : 'var(--text-secondary)'};
  border: 1px solid ${props => props.$active ? 'var(--primary)' : 'var(--border-color)'};
  padding: 12px 24px;
  border-radius: 8px;
  font-family: 'Orbitron', sans-serif;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 18px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 15px rgba(0, 240, 255, 0.2);
  }
  
  &::placeholder {
    color: var(--text-secondary);
    opacity: 0.5;
  }
`;

const Button = styled(motion.button)`
  background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
  color: #fff;
  font-family: 'Orbitron', sans-serif;
  font-size: 14px;
  font-weight: 600;
  padding: 14px 30px;
  border-radius: 8px;
  text-transform: uppercase;
  letter-spacing: 2px;
  border: none;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  margin-right: 10px;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    box-shadow: 0 0 30px rgba(0, 240, 255, 0.5);
  }
`;

const SecondaryButton = styled(Button)`
  background: transparent;
  border: 1px solid var(--border-color);
  
  &:hover:not(:disabled) {
    border-color: var(--primary);
    box-shadow: 0 0 20px rgba(0, 240, 255, 0.3);
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  background: ${props => props.configured 
    ? 'rgba(0, 255, 136, 0.15)' 
    : 'rgba(255, 51, 102, 0.15)'};
  color: ${props => props.configured 
    ? '#00ff88' 
    : '#ff3366'};
  border: 1px solid ${props => props.configured 
    ? 'rgba(0, 255, 136, 0.3)' 
    : 'rgba(255, 51, 102, 0.3)'};
`;

const InfoBox = styled.div`
  background: rgba(0, 240, 255, 0.1);
  border: 1px solid rgba(0, 240, 255, 0.3);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  
  h4 {
    color: var(--primary);
    font-size: 14px;
    margin-bottom: 8px;
  }
  
  p {
    color: var(--text-secondary);
    font-size: 13px;
    line-height: 1.6;
    margin: 0;
  }
`;

const ResultBox = styled.div`
  background: ${props => props.error 
    ? 'rgba(255, 51, 102, 0.1)' 
    : 'rgba(0, 255, 136, 0.1)'};
  border: 1px solid ${props => props.error 
    ? 'rgba(255, 51, 102, 0.3)' 
    : 'rgba(0, 255, 136, 0.3)'};
  border-radius: 8px;
  padding: 16px;
  margin-top: 20px;
  
  pre {
    color: ${props => props.error 
    ? '#ff3366' 
    : '#00ff88'};
    font-size: 13px;
    font-family: monospace;
    white-space: pre-wrap;
    word-break: break-all;
    margin: 0;
  }
`;

const UploadArea = styled.div`
  border: 2px dashed var(--border-color);
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(0, 0, 0, 0.2);
  
  &:hover {
    border-color: var(--primary);
    background: rgba(0, 240, 255, 0.05);
  }
  
  ${props => props.dragging && `
    border-color: var(--primary);
    background: rgba(0, 240, 255, 0.1);
  `}
`;

const UploadIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const FileList = styled.div`
  margin-top: 20px;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  margin-bottom: 10px;
  
  .file-info {
    flex: 1;
    overflow: hidden;
  }
  
  .file-name {
    color: var(--text-primary);
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .file-url {
    color: var(--text-secondary);
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .copy-btn {
    color: var(--primary);
    font-size: 12px;
    cursor: pointer;
    padding: 4px 8px;
    border: 1px solid var(--primary);
    border-radius: 4px;
    margin-left: 10px;
    
    &:hover {
      background: rgba(0, 240, 255, 0.2);
    }
  }
`;

const API_BASE_URL = '/api/v1/user/config';

const ConfigPage = () => {
  const [activeTab, setActiveTab] = useState('api');
  
  const [apiConfig, setApiConfig] = useState({
    app_id: '',
    app_secret: '',
    api_base_url: 'https://api.niwaai.com'
  });
  
  const [ossConfig, setOssConfig] = useState({
    access_key_id: '',
    access_key_secret: '',
    bucket: '',
    endpoint: 'oss-cn-shanghai.aliyuncs.com'
  });
  
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [isApiConfigured, setIsApiConfigured] = useState(false);
  const [isOssConfigured, setIsOssConfigured] = useState(false);
  const [message, setMessage] = useState(null);
  const [testResult, setTestResult] = useState(null);
  
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  
  // 智能体配置
  const [agentConfig, setAgentConfig] = useState({
    llm_api_key: '',
    llm_api_base: 'https://api.openai.com/v1',
    llm_model: 'gpt-3.5-turbo'
  });
  const [isAgentConfigured, setIsAgentConfigured] = useState(false);
  
  const [iceConfig, setIceConfig] = useState({
    access_key_id: '',
    access_key_secret: '',
    region: 'cn-shanghai'
  });
  const [isIceConfigured, setIsIceConfigured] = useState(false);
  
  // DeepSeek AI配置
  const [deepseekConfig, setDeepseekConfig] = useState({
    deepseek_api_key: '',
    deepseek_api_base: 'https://api.deepseek.com/v1',
    deepseek_model: 'deepseek-chat'
  });
  const [isDeepseekConfigured, setIsDeepseekConfigured] = useState(false);

  useEffect(() => {
    fetchConfigs();
    fetchIceConfig();
  }, []);

  const fetchConfigs = async () => {
    try {
      setStatusLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(API_BASE_URL, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.code === 1 && response.data.data) {
        const data = response.data.data;
        setIsApiConfigured(data.configured || false);
        setIsOssConfigured(data.oss_configured || false);
        setIsAgentConfigured(data.agent_configured || false);
        if (data.app_id) {
          setApiConfig(prev => ({
            ...prev,
            app_id: data.app_id || ''
          }));
        }
        if (data.oss_bucket) {
          setOssConfig(prev => ({
            ...prev,
            bucket: data.oss_bucket || '',
            endpoint: data.oss_endpoint || 'oss-cn-shanghai.aliyuncs.com'
          }));
        }
        if (data.llm_api_key) {
          setAgentConfig(prev => ({
            ...prev,
            llm_api_key: data.llm_api_key || '',
            llm_api_base: data.llm_api_base || 'https://api.openai.com/v1',
            llm_model: data.llm_model || 'gpt-3.5-turbo'
          }));
        }
        if (data.deepseek_api_key) {
          setDeepseekConfig(prev => ({
            ...prev,
            deepseek_api_key: data.deepseek_api_key || '',
            deepseek_api_base: data.deepseek_api_base || 'https://api.deepseek.com/v1',
            deepseek_model: data.deepseek_model || 'deepseek-chat'
          }));
        }
        setIsDeepseekConfigured(data.deepseek_configured || false);
      }
    } catch (error) {
      console.error('获取配置失败:', error);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleSaveApi = async () => {
    if (!apiConfig.app_id || !apiConfig.app_secret) {
      setMessage({ type: 'error', text: 'APP_ID和APP_SECRET不能为空' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(API_BASE_URL, apiConfig, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.code === 1) {
        setMessage({ type: 'success', text: 'API配置保存成功！' });
        setIsApiConfigured(true);
      } else {
        setMessage({ type: 'error', text: response.data.msg || '保存失败' });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || '保存失败，请检查后端服务' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestApi = async () => {
    setLoading(true);
    setTestResult(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/test-api`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.code === 1) {
        const data = response.data.data;
        let message = `✅ ${response.data.msg}\n\n`;
        message += `📋 可用接口权限:\n`;
        if (data.permissions && data.permissions.length > 0) {
          data.permissions.forEach(p => {
            message += `  ✓ ${p}\n`;
          });
        }
        message += `\n📊 详细结果:\n`;
        Object.entries(data.details).forEach(([key, value]) => {
          message += `  ${key}: ${value}\n`;
        });
        setTestResult({ success: true, message });
      } else {
        setTestResult({ success: false, message: `❌ ${response.data.msg}` });
      }
    } catch (error) {
      setTestResult({ 
        success: false, 
        message: error.response?.data?.detail || '检测失败，请检查网络连接' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOss = async () => {
    if (!ossConfig.access_key_id || !ossConfig.access_key_secret || !ossConfig.bucket) {
      setMessage({ type: 'error', text: '请填写完整的OSS配置信息' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/oss`, ossConfig, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.code === 1) {
        setMessage({ type: 'success', text: 'OSS配置保存成功！' });
        setIsOssConfigured(true);
      } else {
        setMessage({ type: 'error', text: response.data.msg || '保存失败' });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || '保存失败，请检查后端服务' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = event.target.files || event.dataTransfer.files;
    if (!files.length) return;

    if (!isOssConfigured) {
      setMessage({ type: 'error', text: '请先配置OSS存储' });
      return;
    }

    setUploadLoading(true);
    setMessage(null);

    for (const file of files) {
      const fileId = `${file.name}-${Date.now()}`;
      setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API_BASE_URL}/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({ ...prev, [fileId]: percent }));
          }
        });

        if (response.data.code === 1) {
          setUploadedFiles(prev => [...prev, response.data.data]);
          setMessage({ type: 'success', text: `${file.name} 上传成功！` });
        } else {
          setMessage({ type: 'error', text: response.data.msg || '上传失败' });
        }
      } catch (error) {
        setMessage({ type: 'error', text: `上传失败: ${error.message}` });
      } finally {
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[fileId];
          return newProgress;
        });
      }
    }

    setUploadLoading(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage({ type: 'success', text: '链接已复制到剪贴板' });
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveAgent = async () => {
    if (!agentConfig.llm_api_key) {
      setMessage({ type: 'error', text: 'LLM API Key不能为空' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/agent`, agentConfig, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.code === 1) {
        setMessage({ type: 'success', text: '智能体配置保存成功！' });
        setIsAgentConfigured(true);
      } else {
        setMessage({ type: 'error', text: response.data.msg || '保存失败' });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || '保存失败，请检查后端服务' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDeepSeek = async () => {
    if (!deepseekConfig.deepseek_api_key) {
      setMessage({ type: 'error', text: 'DeepSeek API Key不能为空' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/deepseek`, deepseekConfig, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.code === 1) {
        setMessage({ type: 'success', text: 'DeepSeek AI配置保存成功！' });
        setIsDeepseekConfigured(true);
      } else {
        setMessage({ type: 'error', text: response.data.msg || '保存失败' });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || '保存失败，请检查后端服务' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveIce = async () => {
    if (!iceConfig.access_key_id || !iceConfig.access_key_secret) {
      setMessage({ type: 'error', text: 'Access Key ID和Access Key Secret不能为空' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/v1/mixcut/ice/config?access_key_id=${encodeURIComponent(iceConfig.access_key_id)}&access_key_secret=${encodeURIComponent(iceConfig.access_key_secret)}&region=${iceConfig.region}`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.data.success) {
        setMessage({ type: 'success', text: 'ICE配置保存成功！' });
        setIsIceConfigured(true);
      } else {
        setMessage({ type: 'error', text: response.data.message || '保存失败' });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || '保存失败，请检查后端服务' 
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchIceConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/v1/mixcut/ice/config', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        setIsIceConfigured(response.data.data.configured || false);
      }
    } catch (error) {
      console.error('获取ICE配置失败:', error);
    }
  };

  return (
    <Layout>
      <PageContainer>
        <PageTitle
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          ⚙️ 配置中心
        </PageTitle>
        
        <PageSubtitle
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          配置您的API凭证和云端存储
        </PageSubtitle>

        <TabContainer>
          <Tab $active={activeTab === 'api'} onClick={() => setActiveTab('api')}>
            🔑 API配置
          </Tab>
          <Tab $active={activeTab === 'test'} onClick={() => setActiveTab('test')}>
            🔍 API检测
          </Tab>
          <Tab $active={activeTab === 'oss'} onClick={() => setActiveTab('oss')}>
            ☁️ 云端存储
          </Tab>
          <Tab $active={activeTab === 'ice'} onClick={() => setActiveTab('ice')}>
            🎬 视频混剪ICE
          </Tab>
          <Tab $active={activeTab === 'upload'} onClick={() => setActiveTab('upload')}>
            📤 文件上传
          </Tab>
          <Tab $active={activeTab === 'agent'} onClick={() => setActiveTab('agent')}>
            🤖 智能体配置
          </Tab>
          <Tab $active={activeTab === 'deepseek'} onClick={() => setActiveTab('deepseek')}>
            🧠 DeepSeek AI
          </Tab>
        </TabContainer>

        {activeTab === 'api' && (
          <ConfigCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <ConfigTitle>
              🔐 拟娲数字人API凭证
              {statusLoading ? (
                <span style={{ fontSize: '12px', color: '#a0a0b0' }}>加载中...</span>
              ) : (
                <StatusBadge configured={isApiConfigured}>
                  {isApiConfigured ? '✓ 已配置' : '○ 未配置'}
                </StatusBadge>
              )}
            </ConfigTitle>
          </ConfigCard>
        )}

        {activeTab === 'test' && (
          <ConfigCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <ConfigTitle>
              🔍 API连接检测
            </ConfigTitle>

            <InfoBox>
              <h4>📌 检测说明</h4>
              <p>
                点击下方按钮检测您的API配置是否正确<br/>
                系统将验证APP_ID和APP_SECRET的有效性
              </p>
            </InfoBox>

            <Button
              onClick={handleTestApi}
              disabled={loading || !isApiConfigured}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? '检测中...' : '开始检测'}
            </Button>

            {testResult && (
              <ResultBox error={testResult.success === false}>
                <pre>{testResult.message}</pre>
              </ResultBox>
            )}
          </ConfigCard>
        )}

        {activeTab === 'api' && (
          <ConfigCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <ConfigTitle>
              🔐 拟娲数字人API凭证
              {statusLoading ? (
                <span style={{ fontSize: '12px', color: '#a0a0b0' }}>加载中...</span>
              ) : (
                <StatusBadge configured={isApiConfigured}>
                  {isApiConfigured ? '✓ 已配置' : '○ 未配置'}
                </StatusBadge>
              )}
            </ConfigTitle>

            <InfoBox>
              <h4>📌 如何获取API凭证？</h4>
              <p>
                1. 访问拟娲数字人开发者平台<br/>
                2. 注册/登录您的账号<br/>
                3. 在"应用管理"中创建应用<br/>
                4. 获取AppID和AppSecret<br/>
                5. 在下方填写您的凭证信息
              </p>
            </InfoBox>

            <FormGroup>
              <Label>APP ID</Label>
              <Input
                type="text"
                placeholder="请输入您的APP ID"
                value={apiConfig.app_id}
                onChange={(e) => setApiConfig({ ...apiConfig, app_id: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>APP Secret</Label>
              <Input
                type="password"
                placeholder="请输入您的APP Secret"
                value={apiConfig.app_secret}
                onChange={(e) => setApiConfig({ ...apiConfig, app_secret: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>API地址（可选）</Label>
              <Input
                type="text"
                placeholder="https://api.niwaai.com"
                value={apiConfig.api_base_url}
                onChange={(e) => setApiConfig({ ...apiConfig, api_base_url: e.target.value })}
              />
            </FormGroup>

            <Button
              onClick={handleSaveApi}
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? '保存中...' : '保存配置'}
            </Button>

            {message && (
              <ResultBox error={message.type === 'error'}>
                <pre>{message.text}</pre>
              </ResultBox>
            )}
          </ConfigCard>
        )}

        {activeTab === 'oss' && (
          <ConfigCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <ConfigTitle>
              ☁️ 阿里云OSS存储配置
              {statusLoading ? (
                <span style={{ fontSize: '12px', color: '#a0a0b0' }}>加载中...</span>
              ) : (
                <StatusBadge configured={isOssConfigured}>
                  {isOssConfigured ? '✓ 已配置' : '○ 未配置'}
                </StatusBadge>
              )}
            </ConfigTitle>

            <InfoBox>
              <h4>📌 如何获取OSS配置？</h4>
              <p>
                1. 登录阿里云控制台<br/>
                2. 进入对象存储OSS<br/>
                3. 创建或选择一个Bucket<br/>
                4. 获取AccessKey（RAM访问控制）<br/>
                5. 填写下方配置信息
              </p>
            </InfoBox>

            <FormGroup>
              <Label>AccessKey ID</Label>
              <Input
                type="text"
                placeholder="请输入您的AccessKey ID"
                value={ossConfig.access_key_id}
                onChange={(e) => setOssConfig({ ...ossConfig, access_key_id: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>AccessKey Secret</Label>
              <Input
                type="password"
                placeholder="请输入您的AccessKey Secret"
                value={ossConfig.access_key_secret}
                onChange={(e) => setOssConfig({ ...ossConfig, access_key_secret: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>Bucket名称</Label>
              <Input
                type="text"
                placeholder="请输入Bucket名称"
                value={ossConfig.bucket}
                onChange={(e) => setOssConfig({ ...ossConfig, bucket: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>Endpoint（可选）</Label>
              <Input
                type="text"
                placeholder="oss-cn-shanghai.aliyuncs.com"
                value={ossConfig.endpoint}
                onChange={(e) => setOssConfig({ ...ossConfig, endpoint: e.target.value })}
              />
            </FormGroup>

            <Button
              onClick={handleSaveOss}
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? '保存中...' : '保存配置'}
            </Button>

            {message && (
              <ResultBox error={message.type === 'error'}>
                <pre>{message.text}</pre>
              </ResultBox>
            )}
          </ConfigCard>
        )}

        {activeTab === 'upload' && (
          <ConfigCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <ConfigTitle>
              📤 本地文件上传
            </ConfigTitle>

            <InfoBox>
              <h4>📌 支持的文件类型</h4>
              <p>
                • 视频文件：mp4, webm, mov<br/>
                • 音频文件：mp3, wav, m4a, aac<br/>
                • 图片文件：jpg, png, jpeg<br/>
                上传后将自动存储到您的阿里云OSS
              </p>
            </InfoBox>

            <UploadArea
              onClick={() => document.getElementById('file-input').click()}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleFileUpload(e); }}
            >
              <UploadIcon>📁</UploadIcon>
              <div style={{ color: 'var(--text-secondary)', marginBottom: '10px' }}>
                点击或拖拽文件到此处上传
              </div>
              <div style={{ color: 'var(--primary)', fontSize: '14px' }}>
                {uploadLoading ? '上传中...' : '支持批量上传'}
              </div>
              <input
                id="file-input"
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileUpload}
                accept="video/*,audio/*,image/*"
              />
            </UploadArea>

            {uploadedFiles.length > 0 && (
              <FileList>
                <h4 style={{ color: 'var(--text-secondary)', marginBottom: '15px' }}>
                  已上传文件 ({uploadedFiles.length})
                </h4>
                {uploadedFiles.map((file, index) => (
                  <FileItem key={index}>
                    <div className="file-info">
                      <div className="file-name">{file.filename}</div>
                      <div className="file-url">{file.url}</div>
                    </div>
                    <span
                      className="copy-btn"
                      onClick={() => copyToClipboard(file.url)}
                    >
                      复制链接
                    </span>
                    <span
                      className="copy-btn"
                      style={{ borderColor: '#ff3366', color: '#ff3366' }}
                      onClick={() => removeFile(index)}
                    >
                      删除
                    </span>
                  </FileItem>
                ))}
              </FileList>
            )}

            {message && (
              <ResultBox error={message.type === 'error'}>
                <pre>{message.text}</pre>
              </ResultBox>
            )}
          </ConfigCard>
        )}

        {activeTab === 'agent' && (
          <ConfigCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <ConfigTitle>
              🤖 智能体配置
              {statusLoading ? (
                <span style={{ fontSize: '12px', color: '#a0a0b0' }}>加载中...</span>
              ) : (
                <StatusBadge configured={isAgentConfigured}>
                  {isAgentConfigured ? '✓ 已配置' : '○ 未配置'}
                </StatusBadge>
              )}
            </ConfigTitle>

            <InfoBox>
              <h4>📌 如何获取LLM API凭证？</h4>
              <p>
                1. 访问OpenAI开发者平台或其他LLM服务提供商<br/>
                2. 注册/登录您的账号<br/>
                3. 在API管理中创建API密钥<br/>
                4. 填写下方配置信息
              </p>
            </InfoBox>

            <FormGroup>
              <Label>LLM API Key</Label>
              <Input
                type="password"
                placeholder="请输入您的LLM API Key"
                value={agentConfig.llm_api_key}
                onChange={(e) => setAgentConfig({ ...agentConfig, llm_api_key: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>LLM API地址</Label>
              <Input
                type="text"
                placeholder="https://api.openai.com/v1"
                value={agentConfig.llm_api_base}
                onChange={(e) => setAgentConfig({ ...agentConfig, llm_api_base: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>LLM模型</Label>
              <Input
                type="text"
                placeholder="gpt-3.5-turbo"
                value={agentConfig.llm_model}
                onChange={(e) => setAgentConfig({ ...agentConfig, llm_model: e.target.value })}
              />
            </FormGroup>

            <Button
              onClick={handleSaveAgent}
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? '保存中...' : '保存配置'}
            </Button>

            {message && (
              <ResultBox error={message.type === 'error'}>
                <pre>{message.text}</pre>
              </ResultBox>
            )}
          </ConfigCard>
        )}

        {activeTab === 'deepseek' && (
          <ConfigCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <ConfigTitle>
              🧠 DeepSeek AI 配置
              {statusLoading ? (
                <span style={{ fontSize: '12px', color: '#a0a0b0' }}>加载中...</span>
              ) : (
                <StatusBadge configured={isDeepseekConfigured}>
                  {isDeepseekConfigured ? '✓ 已配置' : '○ 未配置'}
                </StatusBadge>
              )}
            </ConfigTitle>

            <InfoBox>
              <h4>📌 如何获取DeepSeek API凭证？</h4>
              <p>
                1. 访问 DeepSeek 开放平台 <a href="https://platform.deepseek.com" target="_blank" rel="noreferrer" style={{ color: 'var(--primary)' }}>platform.deepseek.com</a><br/>
                2. 注册/登录您的账号<br/>
                3. 在API管理中创建API密钥<br/>
                4. 填写下方配置信息<br/>
                5. 此配置为AI智能体、脚本生成等AI功能提供统一的API调用
              </p>
            </InfoBox>

            <FormGroup>
              <Label>DeepSeek API Key</Label>
              <Input
                type="password"
                placeholder="请输入您的DeepSeek API Key (sk-...)"
                value={deepseekConfig.deepseek_api_key}
                onChange={(e) => setDeepseekConfig({ ...deepseekConfig, deepseek_api_key: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>API地址</Label>
              <Input
                type="text"
                placeholder="https://api.deepseek.com/v1"
                value={deepseekConfig.deepseek_api_base}
                onChange={(e) => setDeepseekConfig({ ...deepseekConfig, deepseek_api_base: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>模型</Label>
              <select
                value={deepseekConfig.deepseek_model}
                onChange={(e) => setDeepseekConfig({ ...deepseekConfig, deepseek_model: e.target.value })}
                style={{
                  width: '100%',
                  padding: '14px 18px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  color: 'var(--text-primary)',
                  fontSize: '16px',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="deepseek-chat">DeepSeek-V3 (deepseek-chat) - 通用对话</option>
                <option value="deepseek-reasoner">DeepSeek-R1 (deepseek-reasoner) - 深度推理</option>
              </select>
            </FormGroup>

            <Button
              onClick={handleSaveDeepSeek}
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? '保存中...' : '保存配置'}
            </Button>

            {message && (
              <ResultBox error={message.type === 'error'}>
                <pre>{message.text}</pre>
              </ResultBox>
            )}
          </ConfigCard>
        )}

        {activeTab === 'ice' && (
          <ConfigCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <ConfigTitle>
              🎬 阿里云智能媒体服务(ICE)配置
              {statusLoading ? (
                <span style={{ fontSize: '12px', color: '#a0a0b0' }}>加载中...</span>
              ) : (
                <StatusBadge configured={isIceConfigured}>
                  {isIceConfigured ? '✓ 已配置' : '○ 未配置'}
                </StatusBadge>
              )}
            </ConfigTitle>

            <InfoBox>
              <h4>📌 如何获取ICE配置？</h4>
              <p>
                1. 登录阿里云控制台<br/>
                2. 进入智能媒体服务(ICE)<br/>
                3. 获取AccessKey ID和AccessKey Secret<br/>
                4. 填写下方配置信息<br/>
                5. 此配置用于视频混剪功能
              </p>
            </InfoBox>

            <FormGroup>
              <Label>AccessKey ID</Label>
              <Input
                type="text"
                placeholder="请输入您的AccessKey ID"
                value={iceConfig.access_key_id}
                onChange={(e) => setIceConfig({ ...iceConfig, access_key_id: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>AccessKey Secret</Label>
              <Input
                type="password"
                placeholder="请输入您的AccessKey Secret"
                value={iceConfig.access_key_secret}
                onChange={(e) => setIceConfig({ ...iceConfig, access_key_secret: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>Region（区域）</Label>
              <Input
                type="text"
                placeholder="cn-shanghai"
                value={iceConfig.region}
                onChange={(e) => setIceConfig({ ...iceConfig, region: e.target.value })}
              />
            </FormGroup>

            <Button
              onClick={handleSaveIce}
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? '保存中...' : '保存配置'}
            </Button>

            {message && (
              <ResultBox error={message.type === 'error'}>
                <pre>{message.text}</pre>
              </ResultBox>
            )}
          </ConfigCard>
        )}

        <ConfigCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <ConfigTitle>📡 服务状态</ConfigTitle>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div style={{ padding: '15px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                <div style={{ color: '#a0a0b0', fontSize: '12px', marginBottom: '5px' }}>后端服务</div>
                <div style={{ color: '#00ff88', fontSize: '14px' }}>● 正常运行</div>
              </div>
              <div style={{ padding: '15px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                <div style={{ color: '#a0a0b0', fontSize: '12px', marginBottom: '5px' }}>API配置</div>
                <div style={{ color: isApiConfigured ? '#00ff88' : '#ff3366', fontSize: '14px' }}>
                  {isApiConfigured ? '● 已配置' : '● 未配置'}
                </div>
              </div>
              <div style={{ padding: '15px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                <div style={{ color: '#a0a0b0', fontSize: '12px', marginBottom: '5px' }}>OSS存储</div>
                <div style={{ color: isOssConfigured ? '#00ff88' : '#ff3366', fontSize: '14px' }}>
                  {isOssConfigured ? '● 已配置' : '● 未配置'}
                </div>
              </div>
              <div style={{ padding: '15px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                <div style={{ color: '#a0a0b0', fontSize: '12px', marginBottom: '5px' }}>智能体配置</div>
                <div style={{ color: isAgentConfigured ? '#00ff88' : '#ff3366', fontSize: '14px' }}>
                  {isAgentConfigured ? '● 已配置' : '● 未配置'}
                </div>
              </div>
              <div style={{ padding: '15px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                <div style={{ color: '#a0a0b0', fontSize: '12px', marginBottom: '5px' }}>ICE混剪服务</div>
                <div style={{ color: isIceConfigured ? '#00ff88' : '#ff3366', fontSize: '14px' }}>
                  {isIceConfigured ? '● 已配置' : '● 未配置'}
                </div>
              </div>
              <div style={{ padding: '15px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                <div style={{ color: '#a0a0b0', fontSize: '12px', marginBottom: '5px' }}>文件上传</div>
                <div style={{ color: isOssConfigured ? '#00ff88' : '#ff3366', fontSize: '14px' }}>
                  {isOssConfigured ? '● 可用' : '● 请先配置OSS'}
                </div>
              </div>
            </div>
        </ConfigCard>
      </PageContainer>
    </Layout>
  );
};

export default ConfigPage;
